import { useMemo } from 'react';
import { ViewPreset } from '@bryntum/schedulerpro';
import { BryntumSchedulerProProps } from '@bryntum/schedulerpro-react';
import { ColumnsState } from '../types/scheduler.types';
import { ResourceInfoCell } from '../components/ResourceInfoCell';

/**
 * Payload for schedule tooltip beforeShow event
 */
interface ScheduleTooltipBeforeShowPayload {
  source?: {
    activeTarget?: HTMLElement;
  };
}

/**
 * Resource record data from Bryntum
 */
interface ResourceRecord {
  id: string;
  name: string;
  description?: string;
  isStandby?: boolean;
  [key: string]: unknown;
}

/**
 * Renderer params from Bryntum column renderer
 */
interface RendererParams {
  record: ResourceRecord;
  value: string;
  column: {
    avatarRendering?: {
      getResourceAvatar: (record: ResourceRecord) => { text: string };
    };
  };
}

interface UseSchedulerConfigOptions {
  /** Event category options for the task edit dropdown */
  eventCategories: string[];
  /** Column visibility state for resource grid columns */
  columnsState: ColumnsState;
  /** Whether user has permission to create events (drag-to-create) */
  canCreateEvents?: boolean;
  /** Whether current user is an adjuster (affects avatar navigation) */
  isAdjuster?: boolean;
}

/**
 * Helper to get initials from a name
 */
function getInitials(name: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Hook to generate Bryntum SchedulerPro configuration
 */
export function useSchedulerConfig({
  columnsState,
  eventCategories = [],
  canCreateEvents = true,
  isAdjuster = false,
}: UseSchedulerConfigOptions) {
  return useMemo(() => {
    // Custom view preset for Dispatch - matches original scheduler
    // Note: shiftIncrement must match defaultSpan to avoid gaps when shifting
    const viewPreset: Partial<ViewPreset> = {
      id: 'dispatchViewPreset',
      name: 'Dispatch View Preset',
      base: 'hourAndDay',
      shiftIncrement: 7,
      shiftUnit: 'day',
      mainUnit: 'day',
      defaultSpan: 7,
      timeResolution: {
        unit: 'minute',
        increment: 1,
      },
    };

    // Custom resource cell renderer with clickable avatar
    const resourceRenderer = ({ record, value }: RendererParams) => {
      const initials = getInitials(value || record.name || '');
      return (
        <ResourceInfoCell
          resourceId={record.id}
          name={value || record.name || ''}
          initials={initials}
          description={record.description}
          isStandby={record.isStandby}
          isAdjuster={isAdjuster}
        />
      );
    };

    // Resource columns - use 'as const' for Bryntum v7 type compatibility
    const columns = [
      {
        type: 'resourceInfo' as const,
        text: 'Resource',
        field: 'name',
        width: 200,
        showImage: false,
        hidden: columnsState['resource']?.hidden ?? false,
        renderer: resourceRenderer,
      },
      {
        text: 'Team',
        field: 'team',
        width: 150,
        hidden: columnsState['team']?.hidden ?? false,
      },
      {
        text: 'County',
        field: 'county',
        width: 150,
        hidden: columnsState['county']?.hidden ?? true,
      },
    ];

    const features = {
      dependencies: { disabled: true },
      eventBuffer: {
        disabled: false,
        // V1 parity: display "Travel Time: {duration}" when hovering a
        // preamble/postamble segment. V1 also suppressed the tooltip for
        // specific classTo cases — that tenant signal doesn't exist in V2,
        // so we show the duration unconditionally.
        tooltipTemplate: ({ duration }: { duration: string }) =>
          `Travel Time: ${duration}`,
      },
      cellEdit: { disabled: true },
      nonWorkingTime: { disabled: false },
      resourceNonWorkingTime: {
        enableMouseEvents: true,
      },
      // Hide schedule tooltip when hovering over non-working time (we have our own tooltip for that)
      scheduleTooltip: {
        listeners: {
          beforeShow({ source }: ScheduleTooltipBeforeShowPayload) {
            // Check if hovering over non-working time element
            const target = source?.activeTarget;
            if (target?.closest('.b-sch-resource-non-working-time')) {
              return false; // Prevent tooltip for non-working time
            }
            return true; // Allow tooltip for working time
          },
        },
      },
      timeSpanHighlight: { disabled: false },
      eventResize: { disabled: false },
      eventDrag: { disabled: false },
      eventDragCreate: { disabled: !canCreateEvents },
      eventTooltip: {
        disabled: false,
      },
      taskEdit: {
        disabled: false,
        editorConfig: {
          title: 'Blocked Time',
          tabBar: false,
          // Limit initial height to 75% of viewport, still resizable
          maxHeight: '75vh',
          // Allow click-outside to close the editor
          autoClose: true,
          modal: false,
          listeners: {
            beforeShow({
              source,
            }: {
              source: {
                widgetMap: Record<
                  string,
                  { value?: unknown; disabled?: boolean }
                >;
              };
            }) {
              const editor = source;
              const startDateField = editor.widgetMap['startDateField'];
              const endDateField = editor.widgetMap['endDateField'];
              const isAllDayField = editor.widgetMap['isAllDay'];

              // Auto-check isAllDay if duration is >= 23:59
              if (
                startDateField?.value &&
                endDateField?.value &&
                isAllDayField
              ) {
                const start = new Date(
                  startDateField.value as string | number | Date
                );
                const end = new Date(
                  endDateField.value as string | number | Date
                );
                const diffMs = end.getTime() - start.getTime();
                const DAY_IN_MS = 86340000; // 23 hours, 59 minutes

                if (diffMs >= DAY_IN_MS) {
                  isAllDayField.value = true;
                }
              }

              // Disable Save until a category is chosen. Bryntum's
              // `required: true` on the combo only flags the field as
              // invalid; the Save button stays enabled and the user can
              // still click through. We mirror that validity onto the
              // button itself so the action is gated, not just nagged.
              const saveButton = editor.widgetMap['saveButton'];
              const categoryField = editor.widgetMap['categoryField'];
              if (saveButton) {
                saveButton.disabled = !categoryField?.value;
              }
            },
          },
        },
        items: {
          generalTab: {
            labelPosition: 'above',
            layout: {
              type: 'vbox',
            },
            items: {
              percentDoneField: false,
              postambleField: false,
              preambleField: false,
              nameField: {
                type: 'text',
                name: 'name',
                label: 'Name',
                labelPosition: 'above',
                weight: 50,
              },
              startDateField: {
                labelPosition: 'above',
              },
              endDateField: {
                labelPosition: 'above',
              },
              categoryField: {
                type: 'combo',
                name: 'category',
                label: 'Category',
                items: eventCategories,
                weight: 400,
                required: true,
                // Keep the Save button in sync with the combo's value so
                // a user can't click Save while the field is still empty.
                // The combo lives inside the form which lives inside the
                // editor; saveButton sits on the editor's bbar — walk up
                // the widget tree until we find it.
                onChange({
                  value,
                  source,
                }: {
                  value: unknown;
                  source: {
                    parent?: unknown;
                  };
                }) {
                  type WidgetNode = {
                    widgetMap?: Record<string, { disabled?: boolean }>;
                    parent?: WidgetNode;
                  };
                  let node: WidgetNode | undefined = source as WidgetNode;
                  while (node) {
                    const saveButton = node.widgetMap?.['saveButton'];
                    if (saveButton) {
                      saveButton.disabled = !value;
                      return;
                    }
                    node = node.parent;
                  }
                },
              },
              isAllDay: {
                type: 'checkbox',
                name: 'isAllDay',
                label: 'All Day',
                labelPosition: 'before',
                weight: 550,
                onChange({
                  value,
                  source,
                }: {
                  value: boolean;
                  source: {
                    parent: {
                      widgetMap: Record<
                        string,
                        { timeField?: { hidden: boolean } }
                      >;
                    };
                  };
                }) {
                  const editor = source.parent;
                  const startDateField = editor.widgetMap['startDateField'];
                  const endDateField = editor.widgetMap['endDateField'];

                  if (startDateField?.timeField) {
                    startDateField.timeField.hidden = value;
                  }
                  if (endDateField?.timeField) {
                    endDateField.timeField.hidden = value;
                  }
                },
              },
              isForced: {
                type: 'checkbox',
                name: 'isForced',
                label: 'Force',
                labelPosition: 'before',
                weight: 600,
              },
              // Created by field (read-only)
              createdBy: {
                type: 'text',
                name: 'createdBy',
                label: 'Created By',
                weight: 650,
                readOnly: true,
              },
              // Edited by field (read-only)
              editedBy: {
                type: 'text',
                name: 'editedBy',
                label: 'Edited By',
                weight: 660,
                readOnly: true,
              },
              // Notes textarea
              notesField: {
                cls: 'menu-text-field',
                type: 'textArea',
                name: 'notes',
                label: 'Notes',
                weight: 1000,
              },
            },
          },
          // Disable unused Bryntum default tabs
          predecessorsTab: false,
          successorsTab: false,
          notesTab: false,
          advancedTab: false,
        },
      },
      // Context menu configuration
      eventMenu: {
        items: {
          cutEvent: false,
          copyEvent: false,
          splitEvent: false,
        },
        processItems({
          eventRecord,
          items,
        }: {
          eventRecord?: { data?: { taskReferenceId?: string } };
          items: Record<string, unknown>;
        }) {
          const taskReferenceId = eventRecord?.data?.taskReferenceId;
          if (taskReferenceId && taskReferenceId !== '') {
            // Task events: hide delete (only blocked time events can be deleted)
            if (items['deleteEvent']) items['deleteEvent'] = false;
          }
        },
      },
      scheduleMenu: {
        disabled: !canCreateEvents,
      },
      // Disable remove/delete options for resource rows and columns
      cellMenu: {
        items: {
          removeRow: false,
        },
      },
      headerMenu: {
        disabled: false,
        items: {
          removeRow: false,
          removeColumn: false,
        },
      },
      timeAxisHeaderMenu: { disabled: false },
      // Current time line indicator - uses Bryntum built-in with timezone support
      timeRanges: {
        showCurrentTimeLine: true,
        currentTimeLineUpdateInterval: 10000, // 10 seconds
        currentDateFormat: 'hh:mm a',
      },
    };

    const keyMap: Record<string, string> = {
      shiftPrevious: '',
      shiftNext: '',
    };

    const subGridConfigs = {
      locked: {
        width: 530,
      },
      normal: {
        flex: 1,
      },
    };

    return {
      viewPreset,
      columns,
      features,
      subGridConfigs,
      keyMap,
      allowOverlap: false,
      barMargin: 2,
      resourceMargin: 2,
      rowHeight: 50,
      autoHeight: false,
      eventStyle: 'filled' as BryntumSchedulerProProps['eventStyle'],
    };
  }, [columnsState, eventCategories, canCreateEvents, isAdjuster]);
}
